<template>
  <div class="flex flex-col gap-2">
    <div
      v-for="(audienceId, key) in internalValue"
      :key="'audience-row-' + key"
      class="flex items-center gap-2"
    >
      <select-field
        class="min-w-0 flex-1"
        :model-value="audienceId || null"
        :options="audiences"
        :reduce="(obj) => obj.id"
        :label="(obj) => obj.name"
        :selectable="(obj) => !obj.disabled && !isUsedElsewhere(obj.id, key)"
        :placeholder="tr('Select target audience')"
        :disabled="disabled"
        :loading="isLoading"
        searchable
        @update:model-value="(value) => selectValue(key, value)"
      >
        <template #option="{ option }">
          <span :title="option.disabled ? (option.disabled_message || null) : null">
            {{ option.name }}
          </span>
        </template>
      </select-field>
      <ui-button
        v-if="!disabled"
        variant="ghost"
        size="icon"
        :aria-label="tr('Remove')"
        @click="() => removeRow(key)"
      >
        <trash-2-icon />
      </ui-button>
    </div>

    <p v-if="!isLoading && audiences.length === 0" class="m-0 text-sm text-muted-foreground">
      {{ tr('There are no target audiences available.') }}
    </p>

    <ui-button
      v-if="showAddButton"
      variant="outline"
      class="w-fit"
      :disabled="disabled || internalValue.includes('')"
      @click="() => addNewRow()"
    >
      <plus-icon />
      {{ tr('Add audience') }}
    </ui-button>
  </div>
</template>

<script>
import { Plus, Trash2 } from '@lucide/vue';

import SelectField from '@/ui/components/SelectField.vue';
import debounce from '@/utils/debounce.js';

/** Saved (Meta) audiences, one searchable select per row; empty rows are not emitted. */
export default {
  name: 'MultipleAudienceSelect',
  components: {
    PlusIcon: Plus,
    SelectField,
    Trash2Icon: Trash2,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    externalAudiences: {
      type: Array,
      required: false,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: () => true,
    },
  },
  emits: [ 'update:modelValue' ],
  data() {
    return {
      debouncedInitSelectedValues: debounce(() => this.initSelectedValues(), 50),
      internalValue: [],
    };
  },
  computed: {
    audiences() {
      return this.externalAudiences;
    },
    showAddButton() {
      return this.audiences.length === 0 || (!this.disabled && this.internalValue.length < this.audiences.length);
    },
  },
  watch: {
    externalAudiences: {
      immediate: true,
      handler() {
        this.debouncedInitSelectedValues();
      },
    },
    isLoading(val) {
      if (!val) {
        this.debouncedInitSelectedValues();
      }
    },
  },
  methods: {
    initSelectedValues() {
      if (this.modelValue.length !== 0 && !this.isLoading) {
        const optionValueList = this.audiences.map(i => i.id);
        const newInternalValue = JSON.parse(JSON.stringify(this.modelValue)).filter(item => {
          if (typeof item === 'object' && 'id' in item) {
            return optionValueList.map(i => i.id).includes(item.id);
          }
          return optionValueList.includes(item);
        });

        const filteredCurrent = this.internalValue.filter(item => !!item);
        const filteredNew = newInternalValue.filter(item => !!item);

        this.internalValue = newInternalValue;
        if (JSON.stringify(filteredCurrent) !== JSON.stringify(filteredNew)) {
          this.emit();
        }
      }
    },
    isUsedElsewhere(id, key) {
      return this.internalValue.some((value, index) => index !== key && value === id);
    },
    addNewRow() {
      this.internalValue.push('');
      this.emit();
    },
    removeRow(rowIndex) {
      this.internalValue.splice(rowIndex, 1);
      this.emit();
    },
    selectValue(key, value) {
      if (this.isUsedElsewhere(value, key)) {
        this.$toast.error(this.tr('Cannot set duplicate target audiences!'));
        return;
      }

      this.internalValue[key] = value;
      this.emit();
    },
    emit() {
      this.$emit('update:modelValue', this.internalValue.filter(item => !!item));
    },
  },
};
</script>
